import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FacultyLayout from './FacultyLayout';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Calendar,
  Eye,
} from 'lucide-react';
import { submissionsAPI } from '../../services/api';

interface Submission {
  id: number;
  title: string;
  category_name: string;
  status: string;
  submitted_date: string;
  reviewed_date?: string;
  feedback?: string;
}

export default function MySubmissions() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const result = await submissionsAPI.getMySubmissions(
          searchTerm || undefined,
          statusFilter === 'all' ? undefined : statusFilter
        );
        if (!result.error && result.data) {
          const data = result.data;
          if (Array.isArray(data) && data.length >= 2) {
            setSubmissions(data[0] || []);
          } else if (Array.isArray(data)) {
            setSubmissions(data);
          }
        }
      } catch (err) {
        console.error('Failed to fetch submissions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, [searchTerm, statusFilter]);

  const filteredSubmissions = submissions;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-4 h-4 mr-1" />
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
            <Clock className="w-4 h-4 mr-1" />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
            <XCircle className="w-4 h-4 mr-1" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  const statusCounts = {
    all: submissions.length,
    pending: submissions.filter((s) => s.status === 'pending').length,
    approved: submissions.filter((s) => s.status === 'approved').length,
    rejected: submissions.filter((s) => s.status === 'rejected').length,
  };

  return (
    <FacultyLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Submissions</h1>
            <p className="text-gray-600 mt-1">
              Track and manage your creative work submissions
            </p>
          </div>
          <Link
            to="/faculty/submit"
            className="mt-4 sm:mt-0 inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold"
          >
            <FileText className="w-5 h-5 mr-2" />
            Submit New Work
          </Link>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 p-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { key: 'all', label: 'All', count: statusCounts.all },
              { key: 'pending', label: 'Pending', count: statusCounts.pending },
              { key: 'approved', label: 'Approved', count: statusCounts.approved },
              { key: 'rejected', label: 'Rejected', count: statusCounts.rejected },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                  statusFilter === tab.key
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title or category..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Submissions List */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-gray-500">Loading submissions...</p>
          </div>
        ) : filteredSubmissions.length > 0 ? (
          <div className="space-y-4">
            {filteredSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {submission.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                        <span className="inline-flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          {submission.category_name}
                        </span>
                        <span className="inline-flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Submitted: {new Date(submission.submitted_date).toLocaleDateString()}
                        </span>
                        {submission.reviewed_date && (
                          <span className="inline-flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Reviewed: {new Date(submission.reviewed_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 sm:mt-0 sm:ml-4">
                      {getStatusBadge(submission.status)}
                    </div>
                  </div>

                  {submission.feedback && (
                    <div
                      className={`p-4 rounded-lg mb-4 ${
                        submission.status === 'approved'
                          ? 'bg-green-50 border border-green-200'
                          : submission.status === 'rejected'
                          ? 'bg-red-50 border border-red-200'
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        Admin Feedback:
                      </p>
                      <p
                        className={`text-sm ${
                          submission.status === 'approved'
                            ? 'text-green-700'
                            : submission.status === 'rejected'
                            ? 'text-red-700'
                            : 'text-gray-700'
                        }`}
                      >
                        {submission.feedback}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-end">
                    <Link
                      to={`/faculty/submissions/${submission.id}`}
                      className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">No submissions found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm
                ? 'Try adjusting your search terms'
                : "You haven't submitted any work yet"}
            </p>
            {!searchTerm && (
              <Link
                to="/faculty/submit"
                className="inline-flex items-center bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-semibold"
              >
                Submit Your First Work
              </Link>
            )}
          </div>
        )}
      </div>
    </FacultyLayout>
  );
}
